
import { LeaderboardData, FunnelStage, RecommendationItem } from "./types";

export const calculateFunnelStages = (data: LeaderboardData[]): FunnelStage[] => {
  const totalProspecting = data.reduce((sum, item) => sum + (item.prospecting_stage_value || 0), 0);
  const totalQualification = data.reduce((sum, item) => sum + (item.qualification_stage_value || 0), 0);
  const totalProposal = data.reduce((sum, item) => sum + (item.proposal_stage_value || 0), 0);
  const totalNegotiation = data.reduce((sum, item) => sum + (item.negotiation_stage_value || 0), 0);
  
  return [
    {
      name: "Prospecting stage",
      value: totalProspecting,
      color: "#8B5CF6",
      textColor: "#FFFFFF",
      widthClass: "w-full"
    }, 
    {
      name: "Qualification stage",
      value: totalQualification,
      color: "#D946EF",
      textColor: "#FFFFFF",
      widthClass: "w-[calc(100%-40px)]"
    }, 
    {
      name: "Proposal stage",
      value: totalProposal,
      color: "#F97316",
      textColor: "#FFFFFF",
      widthClass: "w-[calc(100%-80px)]"
    }, 
    {
      name: "Negotiation stage",
      value: totalNegotiation,
      color: "#0EA5E9",
      textColor: "#FFFFFF",
      widthClass: "w-[calc(100%-120px)]"
    }
  ];
};

export const calculateMetrics = (data: LeaderboardData[], conversionRate: string) => {
  const totalAchievement = data.reduce((sum, item) => sum + (item.achievement || 0), 0);
  const totalTarget = data.reduce((sum, item) => sum + (item.target || 0), 0);
  const totalRevenueGap = data.reduce((sum, item) => sum + (item.revenue_gap || 0), 0);
  const revenueGapPercentage = totalTarget ? (totalRevenueGap / totalTarget * 100).toFixed(2) : "00.00";
  const achievementPercentage = totalTarget ? (totalAchievement / totalTarget * 100).toFixed(2) : "00.00";
  const totalLateStageValue = data.reduce((sum, item) => sum + (item.late_stage_total_value || 0), 0);
  
  const ratioValues = data
    .filter(item => item.late_stage_to_gap_ratio !== null)
    .map(item => item.late_stage_to_gap_ratio || 0);
  
  const avgRatio = ratioValues.length > 0 
    ? (ratioValues.reduce((sum, value) => sum + value, 0) / ratioValues.length).toFixed(2) 
    : "00.00";
  
  const winRatioValues = data
    .filter(item => item.historical_win_ratio !== null)
    .map(item => item.historical_win_ratio || 0);
  
  const avgWinRatio = winRatioValues.length > 0 
    ? (winRatioValues.reduce((sum, value) => sum + value, 0) / winRatioValues.length).toFixed(2) 
    : "00.00";
  
  const lateStageToGapRatio = totalRevenueGap !== 0 
    ? Math.abs(totalLateStageValue / totalRevenueGap).toFixed(2) 
    : "00.00";
  
  const winRatioValue = parseFloat(avgWinRatio);
  let potentialLateStageWinValue;
  
  if (conversionRate === '') {
    potentialLateStageWinValue = totalLateStageValue * (winRatioValue / 100);
  } else {
    const conversionRateValue = parseFloat(conversionRate || "30");
    potentialLateStageWinValue = totalLateStageValue * (winRatioValue / 100) * (conversionRateValue / 100);
  }
  
  return {
    totalAchievement,
    totalTarget,
    totalRevenueGap,
    revenueGapPercentage,
    achievementPercentage,
    totalLateStageValue,
    avgRatio,
    avgWinRatio,
    lateStageToGapRatio,
    potentialLateStageWinValue
  };
};

export const calculateRecommendations = (data: LeaderboardData[]) => {
  const highPerformers = data.filter(item => {
    const achievementPercentage = item.target ? item.achievement / item.target * 100 : 0;
    return achievementPercentage >= 100 && item.account_name && item.late_stage_total_value !== null;
  });
  
  const uniqueAccounts = new Map<string, LeaderboardData>();
  highPerformers.forEach(item => {
    const existingItem = uniqueAccounts.get(item.account_name || '');
    if (!existingItem || (item.late_stage_total_value || 0) > (existingItem.late_stage_total_value || 0)) {
      uniqueAccounts.set(item.account_name || '', item);
    }
  });
  
  const uniqueHighPerformers = Array.from(uniqueAccounts.values());
  const highestSurplus = [...uniqueHighPerformers]
    .sort((a, b) => (b.late_stage_total_value || 0) - (a.late_stage_total_value || 0))
    .map(item => ({
      accountName: item.account_name || 'Unknown',
      lateStageValue: item.late_stage_total_value || 0,
      winRatio: item.historical_win_ratio !== null ? item.historical_win_ratio : undefined
    }));
  
  const totalLateStageValue = highestSurplus.reduce((sum, item) => sum + item.lateStageValue, 0);
  
  const itemsWithAchievementPercentage = data
    .filter(item => item.account_name && item.target > 0 && item.late_stage_total_value !== null)
    .map(item => ({
      ...item,
      achievementPercentage: item.target ? item.achievement / item.target * 100 : 0
    }));
  
  const sortedItems = [...itemsWithAchievementPercentage]
    .sort((a, b) => a.achievementPercentage - b.achievementPercentage);
  
  const percentileCutoff = sortedItems.length > 0 
    ? sortedItems[Math.floor(sortedItems.length * 0.25)].achievementPercentage 
    : 0;
  
  const bottomPerformers = sortedItems.filter(item => item.achievementPercentage <= percentileCutoff);
  
  const lowestPerforming = bottomPerformers
    .map(item => ({
      accountName: item.account_name || 'Unknown',
      lateStageValue: item.late_stage_total_value || 0,
      achievementPercentage: item.achievementPercentage
    }))
    .sort((a, b) => b.lateStageValue - a.lateStageValue)
    .slice(0, 5);
  
  const lowestPerformingTotalValue = lowestPerforming.reduce((sum, item) => sum + item.lateStageValue, 0);
  
  return {
    highestSurplus,
    highestWinRatio: [] as RecommendationItem[],
    lowestPerforming,
    totalLateStageValue,
    lowestPerformingTotalValue
  };
};

export const getFilterLabel = (
  selectedRegion: string, 
  selectedCountry: string, 
  selectedAccountName: string, 
  filterType: "product" | "industry", 
  selectedProductLine: string, 
  selectedIndustry: string
) => {
  const regionLabel = selectedRegion === "All" ? "" : selectedRegion;
  const countryLabel = selectedCountry === "All" ? "" : selectedCountry;
  const accountLabel = selectedAccountName === "All" ? "" : selectedAccountName;
  const typeLabel = filterType === "product" 
    ? selectedProductLine === "All" ? "" : selectedProductLine 
    : selectedIndustry === "All" ? "" : selectedIndustry;
  
  if (!regionLabel && !countryLabel && !accountLabel && !typeLabel) return "";
  
  const filterElements = [regionLabel, countryLabel, accountLabel, typeLabel]
    .filter(Boolean)
    .join(", ");
  
  return `(${filterElements})`;
};
